import { useCallback, useEffect, useId, useState } from "react";

import {
  MONTAGEPRODUKT_NAME_MAX,
  pruefeMontageprodukt,
  staffelKurz,
  type Montageprodukt,
} from "@repo/shared";

import { alsStaffel, alsZeilen, StaffelEditor, type StaffelZeile } from "../components/StaffelEditor";
import { Bestaetigen, useDialog } from "../components/stoffe/Bestaetigen";
import { api, errorMessage } from "../lib/api";

type Eingabe =
  | { modus: "neu" }
  | { modus: "bearbeiten"; produkt: Montageprodukt }
  | { modus: "kopieren"; produkt: Montageprodukt };

const DIALOG_TITEL = {
  neu: "Montageprodukt anlegen",
  bearbeiten: "Montageprodukt bearbeiten",
  kopieren: "Montageprodukt kopieren",
} as const;

const gleich = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Montageeditor: Montageprodukte mit Staffel nach größtem Maß (Minuten je Monteur und Personen). */
export function MontageeditorPage() {
  const [produkte, setProdukte] = useState<Montageprodukt[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const [eingabe, setEingabe] = useState<Eingabe | null>(null);
  const [loeschen, setLoeschen] = useState<Montageprodukt | null>(null);
  const [listenFehler, setListenFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const res = await api.api.admin.montageprodukte.$get();
      if (!res.ok) throw new Error(await errorMessage(res, "Montageprodukte nicht ladbar"));
      const d = await res.json();
      if ("produkte" in d) setProdukte(d.produkte);
      setLadeFehler(null);
    } catch (cause) {
      setLadeFehler(cause instanceof Error ? cause.message : "Montageprodukte nicht ladbar");
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function loeschenAusfuehren() {
    if (!loeschen) return;
    try {
      const res = await api.api.admin.montageprodukte[":id"].$delete({ param: { id: loeschen.id } });
      setListenFehler(res.ok ? null : await errorMessage(res, "Löschen fehlgeschlagen"));
    } catch {
      setListenFehler("Löschen fehlgeschlagen");
    }
    setLoeschen(null);
    await laden();
  }

  const begriff = suche.trim().toLowerCase();
  const sichtbar = (produkte ?? []).filter((p) => p.name.toLowerCase().includes(begriff));

  return (
    <section className="montageeditor">
      <div className="montageeditor__kopf">
        <h1>Montageeditor</h1>
        <button type="button" className="primary stoffe-pill" onClick={() => setEingabe({ modus: "neu" })}>
          + Montageprodukt
        </button>
      </div>
      <input
        type="search"
        className="stoffe-feld stoffe-feld--gefuellt montageeditor__suche"
        placeholder="Bezeichnung suchen"
        aria-label="Bezeichnung suchen"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
      />

      {(listenFehler || ladeFehler) && (
        <p className="error" role="alert">
          {listenFehler ?? ladeFehler}
        </p>
      )}

      {produkte === null ? (
        !ladeFehler && <p className="muted">Lade …</p>
      ) : (
        <div className="tabelle-rahmen stoff-werte__rahmen">
          <table className="table stoff-werte__tabelle">
            <thead>
              <tr>
                <th>Bezeichnung</th>
                <th>Staffel</th>
                <th className="stoffe-aktionen-kopf">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sichtbar.length === 0 ? (
                <tr className="stoffe-leer">
                  <td colSpan={3}>{produkte.length === 0 ? "Noch keine Montageprodukte angelegt." : "Keine Treffer."}</td>
                </tr>
              ) : (
                sichtbar.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td className="montageeditor__staffel">{staffelKurz(p.staffel)}</td>
                    <td>
                      <span className="stoffe-aktionen">
                        <button type="button" className="stoffe-aktion" onClick={() => setEingabe({ modus: "bearbeiten", produkt: p })}>
                          Bearbeiten
                        </button>
                        <button type="button" className="stoffe-aktion" onClick={() => setEingabe({ modus: "kopieren", produkt: p })}>
                          Kopieren
                        </button>
                        <button type="button" className="stoffe-aktion danger" onClick={() => setLoeschen(p)}>
                          Löschen
                        </button>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ProduktDialog
        eingabe={eingabe}
        onSchliessen={() => setEingabe(null)}
        onGespeichert={async () => {
          setEingabe(null);
          setListenFehler(null);
          await laden();
        }}
      />

      <Bestaetigen
        offen={loeschen !== null}
        titel="Montageprodukt löschen"
        text={loeschen ? `Möchten Sie „${loeschen.name}“ wirklich löschen?` : undefined}
        bestaetigen="Löschen"
        gefaehrlich
        onBestaetigen={loeschenAusfuehren}
        onAbbrechen={() => setLoeschen(null)}
      />
    </section>
  );
}

function ProduktDialog({
  eingabe,
  onSchliessen,
  onGespeichert,
}: {
  eingabe: Eingabe | null;
  onSchliessen: () => void;
  onGespeichert: () => Promise<void>;
}) {
  const ref = useDialog(eingabe !== null);
  const titelId = useId();
  const [name, setName] = useState("");
  const [zeilen, setZeilen] = useState<StaffelZeile[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  // Beim Öffnen vorbelegen und die Fehlermeldung zurücksetzen.
  useEffect(() => {
    if (!eingabe) return;
    setFehler(null);
    if (eingabe.modus === "neu") {
      setName("");
      setZeilen(alsZeilen([{ abMm: 0, minuten: 30, personen: 1 }]));
    } else {
      setName(eingabe.produkt.name);
      setZeilen(alsZeilen(eingabe.produkt.staffel));
    }
  }, [eingabe]);

  const kopieGleich = eingabe?.modus === "kopieren" && gleich(name, eingabe.produkt.name);

  async function speichern(event: React.FormEvent) {
    event.preventDefault();
    if (!eingabe || speichert || kopieGleich) return;
    const geprueft = pruefeMontageprodukt({ name, staffel: alsStaffel(zeilen) });
    if ("fehler" in geprueft) return setFehler(geprueft.fehler);
    setFehler(null);
    setSpeichert(true);
    try {
      const res =
        eingabe.modus === "bearbeiten"
          ? await api.api.admin.montageprodukte[":id"].$put({ param: { id: eingabe.produkt.id }, json: geprueft.wert })
          : await api.api.admin.montageprodukte.$post({ json: geprueft.wert });
      if (!res.ok) return setFehler(await errorMessage(res, "Speichern fehlgeschlagen"));
      await onGespeichert();
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Speichern fehlgeschlagen");
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="stoffe-dialog montageeditor__dialog"
      aria-labelledby={titelId}
      onCancel={(e) => {
        e.preventDefault();
        if (!speichert) onSchliessen();
      }}
      onClose={() => {
        if (eingabe) onSchliessen();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !speichert) onSchliessen();
      }}
    >
      {eingabe && (
        <form className="stoffe-dialog__inhalt" onSubmit={speichern}>
          <h2 id={titelId}>{DIALOG_TITEL[eingabe.modus]}</h2>
          <label className="montageeditor__name">
            <span>Bezeichnung</span>
            <input
              type="text"
              className="stoffe-feld stoffe-feld--gefuellt"
              maxLength={MONTAGEPRODUKT_NAME_MAX}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          {kopieGleich && <p className="muted small">Bitte eine andere Bezeichnung wählen.</p>}
          <StaffelEditor zeilen={zeilen} onChange={setZeilen} />
          {fehler && (
            <p className="error" role="alert">
              {fehler}
            </p>
          )}
          <div className="actions stoffe-dialog__aktionen">
            <button type="button" disabled={speichert} onClick={onSchliessen}>
              Abbrechen
            </button>
            <button type="submit" className="primary" disabled={speichert || kopieGleich}>
              Speichern
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
